import React, { createContext, PropsWithChildren, useContext, useMemo, useReducer, useEffect } from "react";
import { getLocalStorageItem } from "@utils/index";

import { TableContextActions } from "@hooks/use-table-context/types";
import {
  INITIAL_CONTEXT_STATE,
  INITIAL_CONTEXT_DISPATCH,
  buildFetchParams,
  USER_HIDDEN_COLUMNS_KEY,
  USER_DEFAULT_HIDDEN_COLUMNS,
  ID_KEY,
  BaseTableStateHandler,
} from "@hooks/use-table-context/utils";
import {
  useCommonTableActions,
  useTableDataFetcher,
} from "@hooks/use-table-context";
import type {
  UserTableContextValue,
  UserTableDispatchValue,
  UserTableFilter,
  UserTableData,
  UserTableContextDispatches,
} from "./types";
import { USER_FILTER_TABLE_KEY, USER_FILTER_SCHEMA_VERSION } from "./utils";
import {
  loadPersistedTableFilterState,
  savePersistedTableFilterState,
} from "@hooks/use-table-context/persistence/table-filter-storage";
import { useGetUsers } from "@hooks/use-get/users";
import { useUpdateUser } from "@hooks/use-update/user";
import { useUserContext } from "@hooks/use-auth-context";
import { useMultiSiteContext } from "@hooks/use-site-context";
import { useScreenAlert } from "@hooks/use-screen-alert";

const UserTableContext = createContext<UserTableContextValue>(INITIAL_CONTEXT_STATE);
const UserTableDispatchContext = createContext<UserTableDispatchValue>(INITIAL_CONTEXT_DISPATCH);

const userTableContextReducer = (
  state: UserTableContextValue,
  action: { type: TableContextActions; payload: any }
) => {
  const stateHandler = new BaseTableStateHandler<UserTableData, UserTableFilter>(state);
  stateHandler[action.type](action.payload as never);
  return stateHandler.getState();
};

export interface UserTableContextProviderProps extends PropsWithChildren {
  /**
   * Distinguishes multiple simultaneous mounts of the User table (e.g. the
   * standalone Users page vs. one or more dashboard widgets) so persisted
   * filters don't overwrite each other. Omit only when a single instance can
   * ever exist at a time.
   */
  instanceId?: string;
}

export const UserTableContextProvider = ({ children, instanceId }: UserTableContextProviderProps) => {
  const { siteId } = useMultiSiteContext();

  // Read once at mount — same timing as the legacy hiddenColumns-only read
  // below, and before UserContext has hydrated (see table-filter-storage.ts).
  const persistedFilterState = useMemo(
    () =>
      loadPersistedTableFilterState<UserTableFilter>(
        USER_FILTER_TABLE_KEY,
        siteId,
        USER_FILTER_SCHEMA_VERSION,
        instanceId,
      ),
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const initialHiddenColumns = useMemo(() => {
    if (persistedFilterState) return persistedFilterState.hiddenColumns;
    const stored = getLocalStorageItem(USER_HIDDEN_COLUMNS_KEY);
    const base = stored ? (() => { try { return JSON.parse(stored); } catch { return USER_DEFAULT_HIDDEN_COLUMNS; } })() : USER_DEFAULT_HIDDEN_COLUMNS;
    // Always hide wp_user_id regardless of stored preferences
    return base.includes("wp_user_id") ? base : [...base, "wp_user_id"];
  }, [persistedFilterState]);

  const [state, dispatch] = useReducer<
    React.Reducer<UserTableContextValue, UserTableContextDispatches>
  >(userTableContextReducer, {
    ...INITIAL_CONTEXT_STATE,
    hiddenColumns: initialHiddenColumns,
    filters: persistedFilterState?.filters ?? INITIAL_CONTEXT_STATE.filters,
    perPage: persistedFilterState?.perPage ?? INITIAL_CONTEXT_STATE.perPage,
  });

  const { loading, getUsers } = useGetUsers();
  const { updateUser, loading: updateUserLoading } = useUpdateUser();
  const { setError } = useScreenAlert();
  const { checkUserAuthentication, user } = useUserContext();

  // Hard delete is not supported for users — no-op keeps the generic table
  // context type satisfied without exposing delete functionality in the UI.
  const noOpDeleteFn = useMemo(
    () => async (_id: string | number) => true,
    []
  );

  // Wrap getUsers to match the { items, total } shape expected by useTableDataFetcher
  const fetchUsers = useMemo(
    () => async (params: { filters: any[]; searchValue: string }) => {
      return await getUsers(params) as { items: UserTableData; total: number };
    },
    [getUsers]
  );

  const updateUserFn = useMemo(
    () => async (wp_user_id: string | number, payload: any): Promise<any> => {
      const ok = await updateUser(Number(wp_user_id), payload);
      if (!ok) setError("Failed to update user.");
      return ok;
    },
    [updateUser, setError]
  );

  const commonActions = useCommonTableActions<UserTableData, UserTableFilter>(
    dispatch,
    fetchUsers,
    noOpDeleteFn,
    updateUserFn,
    state
  );

  const {
    setItems,
    addFilter,
    refreshItems,
    deleteItems,
    editItems,
    removeFilter,
    removeAllFilters,
    setSearchValue,
    clearSearchValue,
    selectItem,
    setPage,
    setPerPage,
    addHiddenColumn,
    removeHiddenColumn,
  } = commonActions;

  const isLoading = useMemo(
    () => state.loading || loading || updateUserLoading,
    [state.loading, loading, updateUserLoading]
  );

  // Gate the initial fetch until the auth token is available in React state.
  // Without this guard, navigating directly to the users URL can fire the
  // fetch before the UserContextProvider effect has set `user`, producing an
  // empty-string Bearer token whose 401 retry also fails (stale closure).
  const authReady = !!user?.authToken;

  useTableDataFetcher({
    state: authReady ? state : { ...state, loading: false },
    loading,
    otherLoadingStates: [updateUserLoading],
    fetchData: fetchUsers,
    getFetchParams: buildFetchParams<UserTableFilter, UserTableData>,
    setItems,
    checkUserAuthentication,
    setError,
  });

  // Persist filters + hidden columns + page size (per user/subsite) whenever
  // any of them change — survives refresh and login.
  useEffect(() => {
    const hiddenColumnsUpdate = state.hiddenColumns.includes(ID_KEY)
      ? state.hiddenColumns
      : [...state.hiddenColumns, ID_KEY];
    savePersistedTableFilterState<UserTableFilter>(
      USER_FILTER_TABLE_KEY,
      siteId,
      USER_FILTER_SCHEMA_VERSION,
      {
        filters: state.filters,
        hiddenColumns: hiddenColumnsUpdate,
        perPage: state.perPage,
      },
      instanceId,
    );
  }, [state.filters, state.hiddenColumns, state.perPage, siteId, instanceId]);

  const dispatchValue = useMemo(
    () => ({
      setItems,
      addFilter,
      removeFilter,
      removeAllFilters,
      setSearchValue,
      clearSearchValue,
      editItems,
      selectItem,
      refreshItems,
      deleteItems,
      setPage,
      setPerPage,
      addHiddenColumn,
      removeHiddenColumn,
    }),
    [
      setItems,
      addFilter,
      removeFilter,
      removeAllFilters,
      setSearchValue,
      clearSearchValue,
      editItems,
      selectItem,
      refreshItems,
      deleteItems,
      setPage,
      setPerPage,
      addHiddenColumn,
      removeHiddenColumn,
    ]
  );

  return (
    <UserTableDispatchContext.Provider value={dispatchValue}>
      <UserTableContext.Provider value={{ ...state, loading: isLoading }}>
        {children}
      </UserTableContext.Provider>
    </UserTableDispatchContext.Provider>
  );
};

export const useUserTableContext = () => {
  const context = useContext(UserTableContext);
  if (!context) {
    throw new Error("useUserTableContext must be used within a UserTableContextProvider");
  }
  return context;
};

export const useUserTableDispatch = () => {
  const dispatchContext = useContext(UserTableDispatchContext);
  if (!dispatchContext) {
    throw new Error("useUserTableDispatch must be used within a UserTableContextProvider");
  }
  return dispatchContext;
};
